import React, { useState } from "react";

import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { IMaskInput } from "react-imask";

import Button from "@/components/Button";
import Dropdown from "@/components/Dropdown";
import InputRadio from "@/components/InputRadio";
import useLocale from "@/hooks/useLocale";
import { submitActivation, submitSurvey } from "@/utils/activation";
import { submitAnswer } from "@/utils/answers";
import { postalCodeMask } from "@/utils/masks";
import { getUserByEmail } from "@/utils/search";

import type { Option } from "@/components/Dropdown";
import type { Field, PublicFormInstance } from "@/utils/form";

const CONCIERGE_PLUS_URL =
  "https://www.ford.ca/schedule-a-test-drive/?gnav=footer-tools";
const SHOW_ASTERISKS = false;
const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

interface FormViewProps {
  formInstance: PublicFormInstance | null;
  currentInstanceKey: string | null;
  onSuccess: (message?: string) => void;
  onError: (error: { title: string; message: string }) => void;
  onReset: () => void;
  onSetConciergePlusUrl: (url: string) => void;
}

const defaultActivationFormErrors = {
  first_name: false,
  last_name: false,
  email: false,
  postal_code: false,
  marketing_opt_in: false,
};

const FormView: React.FC<FormViewProps> = ({
  formInstance,
  currentInstanceKey,
  onSuccess,
  onError,
  onReset,
  onSetConciergePlusUrl,
}) => {
  const { t } = useTranslation();
  const locale = useLocale();

  const [surveyAnswers, setSurveyAnswers] = useState<{
    email: string | null;
    answers: Record<string, string | number | boolean>;
  }>({
    email: null,
    answers: {},
  });
  const [activationFormValues, setActivationFormValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
    postal_code: "",
    marketing_opt_in: false,
  });
  const [activationFormErrors, setActivationFormErrors] = useState(
    defaultActivationFormErrors,
  );
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [conciergePlusError, setConciergePlusError] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const onInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field?: Field,
  ) => {
    const { name, value } = e.target;
    const fieldType = field?.type === "postal_code" ? "postal_code" : null;

    if (fieldType === "postal_code") {
      setSurveyAnswers((prev) => ({
        ...prev,
        answers: {
          ...prev.answers,
          [name]: value.replace(/\s/g, "").toUpperCase(),
        },
      }));
      return;
    }

    setSurveyAnswers((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [name]: value,
      },
    }));
  };

  const onAccept = (value: string) => {
    setActivationFormValues((prev) => ({
      ...prev,
      postal_code: value.toUpperCase(),
    }));
  };

  const onActivationFormChange = (field: string, value: string | boolean) => {
    setActivationFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateActivationForm = () => {
    const errors = {
      email: !activationFormValues.email,
      first_name: !activationFormValues.first_name,
      last_name: !activationFormValues.last_name,
      postal_code: !activationFormValues.postal_code,
    };

    if (Object.values(errors).some((error) => error)) {
      setActivationFormErrors(errors as typeof defaultActivationFormErrors);
      return false;
    }

    return true;
  };

  const onConciergePlusChange = (value: boolean) => {
    setSurveyAnswers((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        CONCIERGE_PLUS: value,
      },
    }));
    setConciergePlusError(false);
  };

  const handleSubmitActivation = async () => {
    if (
      currentInstanceKey &&
      activationFormValues.first_name &&
      activationFormValues.last_name
    ) {
      const activationKey = currentInstanceKey.replace(
        "FORD_MODEL_E_SURVEY",
        "FORD_MODEL_E_DIGITAL_ACTIVATION",
      );

      let foundEntry = null;
      try {
        const searchResult = await getUserByEmail(
          activationFormValues.email,
          activationKey,
        );
        foundEntry = { ...searchResult, instance_key: activationKey };
      } catch (_error) {
        // Do nothing
      }

      if (!foundEntry) {
        try {
          await submitActivation({
            instance_key: activationKey,
            email: activationFormValues.email,
            first_name: activationFormValues.first_name,
            last_name: activationFormValues.last_name,
            postal_code: activationFormValues.postal_code,
            marketing_opt_in: activationFormValues.marketing_opt_in,
            locale: locale,
          });
        } catch (err) {
          console.log("Error submitting activation form:", err);
          Sentry.captureException(err);
          setIsFormSubmitting(false);
          onError({
            title: "Could not submit activation form.",
            message: (err as Error).message,
          });
          return;
        }
      } else {
        const newAnswerValue = activationFormValues.marketing_opt_in
          ? "A"
          : "H";
        const marketingOptInAnswer = foundEntry.answers.find(
          (a: { field_key: string; value: string }) => a.field_key === "A799E",
        );
        if (
          !marketingOptInAnswer ||
          marketingOptInAnswer.value !== newAnswerValue
        ) {
          await submitAnswer({
            instance_key: foundEntry.instance_key,
            email: activationFormValues.email,
            field_key: "A799E",
            value: newAnswerValue,
          });
        }
      }
    }
  };

  const handleSubmitConciergePlus = async () => {
    setActivationFormErrors(defaultActivationFormErrors);

    if (!currentInstanceKey) {
      throw new Error(
        "Survey key is not available. Please refresh the page and try again.",
      );
    }

    const activationFormValid = validateActivationForm();
    if (!activationFormValid) return;

    onSetConciergePlusUrl(CONCIERGE_PLUS_URL);
    setIsFormSubmitting(true);

    try {
      await handleSubmitActivation();

      await submitSurvey(currentInstanceKey, activationFormValues.email, {
        CONCIERGE_PLUS: true,
      });

      onSuccess();
    } catch (err) {
      console.error("Error in handleSubmitConciergePlus:", err);
      Sentry.captureException(err);
      setIsFormSubmitting(false);
      onError({
        title: "Could not submit ConciergePlus-only form.",
        message: (err as Error).message,
      });
    }
  };

  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsFormSubmitting(true);
    setConciergePlusError(false);

    const activationFormValid = validateActivationForm();
    if (!activationFormValid) {
      setIsFormSubmitting(false);
      return;
    }

    if (surveyAnswers.answers["CONCIERGE_PLUS"] === undefined) {
      setConciergePlusError(true);
      setIsFormSubmitting(false);
      document
        .getElementById("concierge-plus-error")
        ?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    try {
      if (surveyAnswers.answers["CONCIERGE_PLUS"] === true) {
        onSetConciergePlusUrl(CONCIERGE_PLUS_URL);
      }

      if (!currentInstanceKey) {
        throw new Error(
          "Survey key is not available. Please refresh the page and try again.",
        );
      }

      if (!activationFormValues.email) {
        throw new Error(
          "Email is required. Please fill in your email address.",
        );
      }

      console.log("Submitting survey with:", {
        survey_key: currentInstanceKey,
        email: activationFormValues.email,
        answers_count: Object.keys(surveyAnswers.answers).length,
      });

      await submitSurvey(
        currentInstanceKey,
        activationFormValues.email,
        surveyAnswers.answers,
      );

      try {
        await handleSubmitActivation();
      } catch (err) {
        onError({
          title:
            "Could not submit activation form. Survey data has been submitted.",
          message: (err as Error).message,
        });
        onSuccess(
          locale === "en"
            ? "An error happened while submitting the activation form."
            : "Une erreur est survenue lors de la soumission du formulaire d'activation.",
        );
        return;
      }

      onSuccess();
    } catch (err) {
      console.error("Error submitting survey:", err);
      Sentry.captureException(err);
      setIsFormSubmitting(false);
      onError({
        title: "Could not submit survey.",
        message: (err as Error).message,
      });
    }
  };

  const getFieldTypeFromData = (fieldData: Field) => {
    const { key, choices = null, type = null } = fieldData || {};

    if (key === "DATE") {
      return "date";
    }
    if (key === "comments") {
      return "textarea";
    }
    if (type === "rating") {
      return "rating";
    }
    if (type === "yesno") {
      return "yes-no";
    }
    if (type === "choices" && key === "STATE") {
      return "select";
    }
    if (type === "postal_code") {
      return "postal_code";
    }
    if (choices !== null) {
      return "radio";
    }
    return "text";
  };

  if (formInstance?.fields.length === 0) {
    return (
      <div className="py-6 font-bold text-lg text-lightgrey animate-pulse">
        {t("survey.wait")}
      </div>
    );
  }

  if (!formInstance) {
    return null;
  }

  return (
    <form method="POST" onSubmit={onFormSubmit}>
      <div>
        <h1 className="text-lg font-black uppercase text-center mb-2">
          {t("survey.title")}
        </h1>

        <fieldset className="mt-[30px] pb-6 border-b-2 border-lightgrey">
          <input
            value={activationFormValues.first_name}
            onChange={(e) =>
              onActivationFormChange("first_name", e.target.value)
            }
            name="first_name"
            autoComplete="given-name"
            placeholder={`${t("signup.first_name")}*`}
            type="text"
            className={`mt-2 placeholder:text-gray placeholder:text-center placeholder:font-normal text-blue rounded-[5px] w-full font-semibold py-2 px-2 border-[1px] ${
              activationFormErrors.first_name ? "border-red" : "border-blue"
            }`}
            required
          />

          <input
            value={activationFormValues.last_name}
            onChange={(e) =>
              onActivationFormChange("last_name", e.target.value)
            }
            name="last_name"
            autoComplete="family-name"
            type="text"
            placeholder={`${t("signup.last_name")}*`}
            className={`mt-2 placeholder:text-gray placeholder:text-center placeholder:font-normal text-blue rounded-[5px] w-full font-semibold py-2 px-2 border-[1px] ${
              activationFormErrors.last_name ? "border-red" : "border-blue"
            }`}
            required
          />

          <input
            value={activationFormValues.email}
            onChange={(e) =>
              onActivationFormChange(
                "email",
                e.target.value.toLowerCase().trim(),
              )
            }
            name="email"
            autoComplete="email"
            type="email"
            placeholder={`${t("signup.email")}*`}
            className={`mt-2 placeholder:text-gray placeholder:text-center placeholder:font-normal text-blue rounded-[5px] w-full font-semibold py-2 px-2 border-[1px] ${
              activationFormErrors.email ? "border-red" : "border-blue"
            }`}
            required
          />

          <IMaskInput
            value={activationFormValues.postal_code}
            mask={postalCodeMask}
            name="postal_code"
            autoCapitalize="characters"
            required
            type="text"
            placeholder={`${t("signup.postal_code")}*`}
            className={`mt-2 placeholder:text-gray placeholder:text-center placeholder:font-normal text-blue rounded-[5px] w-full font-semibold py-2 px-2 border-[1px] ${
              activationFormErrors.postal_code ? "border-red" : "border-blue"
            }`}
            onAccept={onAccept}
          />

          <label className="checkbox flex mt-7 text-[#747474] text-[9px]">
            <input
              type="checkbox"
              name="marketing_opt_in"
              checked={activationFormValues.marketing_opt_in}
              onChange={(e) =>
                onActivationFormChange("marketing_opt_in", e.target.checked)
              }
            />
            <span
              dangerouslySetInnerHTML={{
                __html: t("signup.optIn"),
              }}
            />
          </label>

          <div className="flex justify-center mt-6">
            <Button
              onClick={handleSubmitConciergePlus}
              loading={isFormSubmitting}
              design="white"
              className="border-2 border-blue"
            >
              {t("survey.conciergePlus.participate")}
            </Button>
          </div>

          {Object.values(activationFormErrors).some((v) => !!v) && (
            <div className="text-red text-center mt-2 text-lg font-bold">
              {t("survey.conciergePlus.errorMessage")}
            </div>
          )}
        </fieldset>

        {formInstance.fields.map((field) => (
          <fieldset
            key={field.key}
            className="mt-6 pb-6 border-b-2 border-lightgrey"
          >
            <label htmlFor={field.key} className="text-lg font-bold mb-4">
              <span
                dangerouslySetInnerHTML={{
                  __html: field.label[locale],
                }}
              />
              {!field.optional && SHOW_ASTERISKS && (
                <span className="text-sm">*</span>
              )}
            </label>

            {getFieldTypeFromData(field) === "date" && (
              <input
                name={field.key}
                type="date"
                className="w-full border-blue border-2 rounded-lg bg-white"
                required={!field.optional}
                value={(surveyAnswers.answers[field.key] as string) || today}
                onChange={onInputChange}
              />
            )}

            {getFieldTypeFromData(field) === "select" && (
              <Dropdown
                name={field.key}
                options={(field.choices || []) as Option[]}
                required={!field.optional}
                value={surveyAnswers.answers[field.key] as string}
                variant="outline"
                autocomplete="off"
                onChange={(value: string) =>
                  onInputChange(
                    {
                      target: { name: field.key, value },
                    } as unknown as React.ChangeEvent<HTMLInputElement>,
                    field,
                  )
                }
              />
            )}

            {getFieldTypeFromData(field) === "radio" && (
              <div className={field.choices?.length === 10 ? "flex" : ""}>
                {field.choices?.map((choice) => (
                  <InputRadio
                    key={choice.value}
                    label={choice.label[locale]}
                    value={choice.value}
                    name={field.key}
                    checked={surveyAnswers.answers[field.key] === choice.value}
                    inline={field.choices?.length === 10}
                    className={
                      field.choices?.length === 10
                        ? "mt-4 text-center grow flex flex-col items-center justify-center"
                        : "mt-4"
                    }
                    required={!field.optional}
                    onChange={(value: string) =>
                      onInputChange(
                        {
                          target: { name: field.key, value },
                        } as unknown as React.ChangeEvent<HTMLInputElement>,
                        field,
                      )
                    }
                  />
                ))}
              </div>
            )}

            {getFieldTypeFromData(field) === "textarea" && (
              <textarea
                className="w-full border-blue border-2 rounded-lg bg-white p-3"
                name={field.key}
                style={{ height: "150px" }}
                value={(surveyAnswers.answers[field.key] as string) || ""}
                onChange={onInputChange}
              />
            )}

            {getFieldTypeFromData(field) === "rating" && (
              <div className="flex">
                {RATINGS.map((rating) => (
                  <InputRadio
                    key={rating}
                    label={rating.toString()}
                    value={rating.toString()}
                    name={field.key}
                    checked={
                      surveyAnswers.answers[field.key] === rating.toString()
                    }
                    className="mt-4 text-center grow flex flex-col items-center justify-center"
                    inline
                    onChange={(value: string) =>
                      onInputChange(
                        {
                          target: { name: field.key, value },
                        } as unknown as React.ChangeEvent<HTMLInputElement>,
                        field,
                      )
                    }
                  />
                ))}
              </div>
            )}

            {getFieldTypeFromData(field) === "yes-no" && (
              <>
                <InputRadio
                  label={t("survey.yes")}
                  value="yes"
                  name={field.key}
                  className="mt-4"
                  checked={surveyAnswers.answers[field.key] === "true"}
                  onChange={(value: string) =>
                    onInputChange(
                      {
                        target: { name: field.key, value },
                      } as unknown as React.ChangeEvent<HTMLInputElement>,
                      field,
                    )
                  }
                />
                <InputRadio
                  label={t("survey.no")}
                  value="no"
                  name={field.key}
                  className="mt-4"
                  checked={surveyAnswers.answers[field.key] === "false"}
                  onChange={(value: string) =>
                    onInputChange(
                      {
                        target: { name: field.key, value },
                      } as unknown as React.ChangeEvent<HTMLInputElement>,
                      field,
                    )
                  }
                />
              </>
            )}

            {getFieldTypeFromData(field) === "postal_code" && (
              <IMaskInput
                mask={postalCodeMask}
                name={field.key}
                required={!field.optional}
                type="text"
                className="w-full py-2 px-3 border-blue border-2 rounded-lg bg-white"
                value={(surveyAnswers.answers[field.key] as string) || ""}
                onAccept={onAccept}
                onChange={(e) =>
                  onInputChange(
                    e as unknown as React.ChangeEvent<HTMLInputElement>,
                    field,
                  )
                }
              />
            )}

            {getFieldTypeFromData(field) === "text" && (
              <input
                name={field.key}
                type="text"
                className="w-full py-2 px-3 border-blue border-2 rounded-lg bg-white"
                required={!field.optional}
                value={(surveyAnswers.answers[field.key] as string) || ""}
                onChange={onInputChange}
              />
            )}
          </fieldset>
        ))}

        <fieldset className="mt-6 pb-6">
          <label
            className="block text-lg w-full max-w-[595px] text-center mx-auto font-bold mb-[22px]"
            dangerouslySetInnerHTML={{
              __html: t("survey.conciergePlus.title"),
            }}
          />
          <div className="flex justify-center gap-4">
            <button
              type="button"
              className={`w-[180px] h-[44px] uppercase font-bold text-sm rounded-md transition-all duration-300 ease-in-out ${
                surveyAnswers.answers["CONCIERGE_PLUS"] === true
                  ? "bg-black text-white"
                  : "bg-white border-[2px] border-solid border-black text-black"
              }${conciergePlusError ? " border-red text-red" : ""}`}
              onClick={() => onConciergePlusChange(true)}
            >
              {t("survey.conciergePlus.yes")}
            </button>
            <button
              type="button"
              className={`w-[180px] h-[44px] uppercase font-bold text-sm rounded-md transition-all duration-300 ease-in-out ${
                surveyAnswers.answers["CONCIERGE_PLUS"] === false
                  ? "bg-black text-white"
                  : "bg-white border-[2px] border-solid border-black text-black"
              }${conciergePlusError ? " border-red text-red" : ""}`}
              onClick={() => onConciergePlusChange(false)}
            >
              {t("survey.conciergePlus.no")}
            </button>
          </div>
          {conciergePlusError ? (
            <p
              id="concierge-plus-error"
              className="text-red text-center mt-2 text-lg font-bold"
            >
              {t("survey.conciergePlus.error")}
            </p>
          ) : (
            <span className="h-10" />
          )}
        </fieldset>

        <div className="text-center mt-10 pb-2">
          <Button className="mx-auto" type="submit" loading={isFormSubmitting}>
            {t("survey.submit")}
          </Button>
        </div>

        <div className="text-center mt-2 pb-10">
          <Button
            onClick={onReset}
            type="button"
            design="white"
            className="mx-auto border-2 border-blue"
          >
            {t("survey.reset")}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default FormView;
